import { isValidPhoneNumber } from 'libphonenumber-js';
import { DEFAULT_LIST_ID } from '../defaults';
import { FieldDataType } from '../enums/FieldDataType';
import { ContactAddress, ContactFieldValue } from '../types/ContactField';
import { ViewProperty } from '../types/ViewProperty';
import { compose } from './functional';
import { sanitizeAllTags } from './sanitizeHtml';

export interface IErrorFormValidation {
    /*
        TIP: fieldId & field are required for form fields where there can be multiple inputs
        whcih make up the form field (grid filters, contact address, etc.)
     */
    fieldId: number;
    field: string;
    message: string;
    isFormError: boolean;
}

class ErrorFormValidation extends Error implements IErrorFormValidation {
    readonly fieldId: number;
    readonly field: string;
    readonly isFormError: boolean;

    constructor(
        field: string,
        message: string | undefined = 'Required field cannot be empty.',
        fieldId?: number,
    ) {
        super(message);
        this.fieldId = fieldId || NaN;
        this.field = field;
        this.isFormError = true;
    }
}

function withCatchFormError<T extends (...args: any) => any>(fn: T) {
    return function (fieldName: string, ...args: any[]): ReturnType<T> {
        console.log('withCatchFormError', fieldName, args);
        try {
            /* 
                BUG: Normally would include "this" in place of "null" but TypeScript
                sees "this" as having "implied any type" in current implmentation
             */
            return fn.apply(null, args);
        } catch (error: any) {
            throw new ErrorFormValidation(fieldName, error.message);
        }
    };
}

function withCatchFormErrorMultiField<T extends (...args: any) => any>(fn: T) {
    return function (fieldName: string, fieldId: number, ...args: any[]): ReturnType<T> {
        try {
            /* 
                BUG: Normally would include "this" in place of "null" but TypeScript
                sees "this" as having "implied any type" in current implmentation
             */
            return fn.apply(null, args);
        } catch (error: any) {
            throw new ErrorFormValidation(fieldName, error.message, fieldId);
        }
    };
}

/*  -------------------------------------------
    UTILITIES - 
    composable functions that serve a single purpose i.e.: to validate a single required aspect of the value,
    if validation fails throws an error describing the error.
*/

/**
 * sanitizeAllHtmlFromContactAddress
 *
 * @description
 * Removes all HTML tags from each contact address fields
 */
const sanitizeAllHtmlFromContactAddress = (address: ContactAddress): ContactAddress => {
    return Object.entries(address).reduce<ContactAddress>(
        (acc, ent) => {
            return {
                ...acc,
                ...{
                    [ent[0]]:
                        ent[0] !== 'addressId'
                            ? sanitizeAllTags(ent[1]?.toString()).trim()
                            : ent[1],
                },
            };
        },
        {
            street: '',
            city: '',
            province: '',
            country: '',
            postalCode: '',
            addressId: NaN,
        },
    );
};

/**
 * stringifyContactAddress
 *
 * @description
 * Creates a string from the property values of a contact address object,
 * excluding "addressId" property
 *
 * @param address
 */
const stringifyContactAddress = (address: ContactAddress): string => {
    return Object.entries(address)
        .map(ent => {
            return ent[0] !== 'addressId' && ent[1] ? `${ent[1]}` : '';
        })
        .join(' ');
};

/**
 * checkStringIsNotEmpty
 *
 * @description
 * Verifies that the string value has a length greater than 0
 */
const checkStringIsNotEmpty = (value: string): string => {
    if (value === undefined || value === null || value.length === 0) {
        throw new Error('Required field cannot be empty.');
    }

    return value;
};

/**
 * checkStringArrayIsNotEmpty
 *
 * @description
 * Verifies that the string value has a length greater than 0
 */
const checkStringArrayIsNotEmpty = (values: string[]): string[] => {
    if (values === undefined || values === null || values.length === 0) {
        throw new Error('Required field cannot be empty.');
    }

    return values;
};

/**
 * checkContactAddressIsNotEmpty
 *
 * @description
 * Verifies that the contact address object has at least one field which contains
 * a value
 */
const checkContactAddressIsNotEmpty = (value: ContactAddress): ContactAddress => {
    if (stringifyContactAddress(value).trim().length === 0) {
        throw new Error('A contact address requires at least one field which contains a value.');
    }

    return value;
};

/**
 * checkNumberIsNotEmpty
 *
 * @description
 * Verifies that a number value is not value held by DEFAULT_COMMON_FORM_NUMBER
 */
const checkNumberIsNotEmpty = (value: number): number => {
    if (value === undefined || value === null || isNaN(value)) {
        throw new Error('Required field cannot be empty.');
    }

    return value;
};

/**
 * checkNumberIsNotDefaultListId
 *
 * @description
 * Verifies that a number does not match the value of DEFAULT_LIST_ID
 */
const checkNumberIsNotDefaultListId = (value: number): number => {
    if (value === DEFAULT_LIST_ID) {
        throw new Error('Selected list cannot be the default list.');
    }

    return value;
};

/**
 * checkNumberIsSmallerThan
 *
 * @param comparator
 * Number that will be compared against the validator's main value; expected to
 * be a smaller value than main value.
 */
const checkNumberIsSmallerThan =
    (comparator: number) =>
    (value: number): number => {
        if (!(comparator > value)) {
            throw new Error('Minimum range value must be smaller than maximum range value.');
        }

        return value;
    };

/**
 * checkDateStringIsSmallerThan
 *
 * @param comparator
 * Date that will be compared against the validator's main value; expected to
 * be a smaller value than main value.
 */
const checkDateStringIsSmallerThan =
    (comparator: string) =>
    (value: string): string => {
        const dateStringMin = new Date(value).valueOf();
        const dateStringMax = new Date(comparator).valueOf();

        if (!(dateStringMax > dateStringMin)) {
            throw new Error('Start date must be after end date');
        }

        return value;
    };

/*  -------------------------------------------
    COMPOSED FUNCTIONS -
    functions made up of multiple "utilities"
    
    TIP: order of functions given to compose is important
*/

/*
 * composedValidateStringInput
 *
 * @description
 * Sanitization and validation pipeline for string input fields
 */
const composedValidateStringInput = compose(sanitizeAllTags, checkStringIsNotEmpty) as (
    value: string,
) => string;

/*
 * composedValidateStringArrayInput
 *
 * @description
 * Validation pipeline for string array input fields
 */
const composedValidateStringArrayInput = compose(checkStringArrayIsNotEmpty) as (
    values: string[],
) => string[];

/**
 * composedValidateNumberInput
 *
 * @description
 * Validation pipeline for number fields which default to DEFAULT_COMMON_FORM_NUMBER
 */
const composedValidateNumberInput = compose(checkNumberIsNotEmpty) as (value: number) => number;

/**
 * composedValidateContactAddress
 *
 * @description
 * Sanitization and validation pipeline for contact address string input fields
 */
const composedValidateContactAddress = compose(
    sanitizeAllHtmlFromContactAddress,
    checkContactAddressIsNotEmpty,
) as (value: ContactAddress) => ContactAddress;

/**
 * composedValidateSelectedListId
 *
 * @description
 * Validation pipeline for selected list id's
 */
const composedValidateSelectedListId = compose(
    checkNumberIsNotEmpty,
    checkNumberIsNotDefaultListId,
) as (value: number) => number;

/**
 * composedValidateMinRangeNumber
 *
 * @description
 * Validation pipeline for number form fields where the validator's main value
 * should be smaller than the value given as comparator
 */
const composedValidateMinRangeNumber = (comparator: number) =>
    compose(checkNumberIsNotEmpty, checkNumberIsSmallerThan(comparator)) as (
        value: number,
    ) => number;

/**
 * composedValidateMinRangeDateString
 *
 * @description
 * Validation pipeline for date string form fields where the validator's main value
 * should be smaller than the value given as comparator
 */
const composedValidateMinRangeDateString = (comparator: string) =>
    compose(checkStringIsNotEmpty, checkDateStringIsSmallerThan(comparator)) as (
        value: string,
    ) => string;

/*  -------------------------------------------
    VALIDATORS -
    exported validators
*/

/**
 * validateStringInput
 *
 * @description
 * Validation function which sanitizes and ensures a valid value for the given string field
 */
export const validateStringInput = withCatchFormError(composedValidateStringInput);

/**
 * validateNumberInput
 *
 * @description
 * Validation function which ensures a valid value for the given number field
 */
export const validateNumberInput = withCatchFormError(composedValidateNumberInput);

/**
 * validateSelectedListId
 *
 * @description
 * Validation function which ensures the value passed is a valid list id
 */
export const validateSelectedListId = withCatchFormError(composedValidateSelectedListId);

/**
 * validateContactFieldValue
 *
 * @description
 * Validation function which sanitizes and ensures a valid value for the given
 * contact address object
 */
export const validateContactFieldValue = withCatchFormError(
    (value: ContactFieldValue): ContactFieldValue => {
        switch (typeof value) {
            case 'object':
                return composedValidateContactAddress(value);
            case 'string':
            default:
                /* default expects string */
                return composedValidateStringInput(value);
        }
    },
);

/**
 * validateGridFilterItemValue
 *
 * @description
 * Validation for the ConversationGridFilterItem property "value"
 *
 * IMPORTANT: do not use with "value2" property
 */
export const validateGridFilterItemValue = withCatchFormErrorMultiField(
    (
        fieldType: FieldDataType,
        operand: string,
        value: string | number,
        value2: string | number,
        values: string[],
    ): string => {
        if (operand === 'is not empty') return value.toString();

        switch (fieldType) {
            case FieldDataType.date:
                return operand === 'between'
                    ? composedValidateMinRangeDateString(value2 as string)(value as string)
                    : composedValidateStringInput(value as string);
            case FieldDataType.integer:
            case FieldDataType.float:
                return operand === 'between'
                    ? composedValidateMinRangeNumber(value2 as number)(value as number).toString()
                    : composedValidateNumberInput(value as number).toString();
            case FieldDataType.tags:
            case FieldDataType.users:
            case FieldDataType.enum:
                return value as string;
            case FieldDataType.string:
            case FieldDataType.text:
            default:
                /* 
                default expects string 

                TIP: string grid filter fields do not have the operand "between", 
                should not have ranged values
             */
                return composedValidateStringInput(value as string);
        }
    },
);

/**
 * validateGridFilterItemValue2
 *
 * @description
 * Validation for the ConversationGridFilterItem property "value2"
 *
 * IMPORTANT: do not use with "value" property
 */
export const validateGridFilterItemValue2 = withCatchFormErrorMultiField(
    (operand: string, value2: string | number): string => {
        /* 
        TIP: comparisons between min & max range values are done when validating grid filter property "value",
        no need to run that validation again. If operand holds value indicating a range of values, ensure the
        value is valid.
    */
        return operand === 'between'
            ? composedValidateStringInput(value2.toString())
            : value2.toString();
    },
);

export function validateViewProperties(value: ViewProperty[]): ViewProperty[] {
    if (value.length < 1) {
        throw new ErrorFormValidation('viewProperties', 'View properties cannot be empty');
    }

    return value;
}

/**
 * validateGridFilterItemValues
 *
 * @description
 * Validation for the ConversationGridFilterItem property "values"
 *
 */
export const validateGridFilterItemValues = withCatchFormErrorMultiField(
    (fieldType: FieldDataType, values: string[]): string[] => {
        switch (fieldType) {
            case FieldDataType.tags:
            case FieldDataType.users:
            case FieldDataType.enum:
                return composedValidateStringArrayInput(values);
            default:
                /*default expects string[]*/
                return [];
        }
    },
);

// Form validation for creating/editing user (User Settings Page)
const checkPhoneNumberStringNaN = (value: string): string => {
    if (value.length < 10 || !isValidPhoneNumber(value, 'US')) {
        throw new Error(`Please enter valid phone number`);
    }
    return value;
};

/*
 * composedValidatePhoneNumberStringInput
 *
 * @description
 * Sanitization and validation pipeline for string input fields that only contain numbers (i.e. string phone number input)
 */
const composedValidatePhoneNumberStringInput = compose(
    sanitizeAllTags,
    checkStringIsNotEmpty,
    checkPhoneNumberStringNaN,
) as (value: string) => string;

export const validatePhoneNumberInput = withCatchFormError(composedValidatePhoneNumberStringInput);

const checkValidEmail = (value: string): string => {
    //let regex = new RegExp('[a-z0-9]+@[a-z]+\.[a-z]{2,3}'); => old regex
    let regex = new RegExp(
        '^[a-zA-Z0-9.!#$%&*+/=?^_`{|}~-]+@[a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?(?:.[a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?)*$',
    );

    if (!regex.test(value)) {
        throw new Error('Please input valid email address');
    }

    return value;
};

/*
 * composedValidateEmailStringInput
 *
 * @description
 * Sanitization and validation pipeline for string input fields that only contains valid email address
 */
const composedValidateEmailStringInput = compose(
    sanitizeAllTags,
    checkStringIsNotEmpty,
    checkValidEmail,
) as (value: string) => string;

export const validateEmailInput = withCatchFormError(composedValidateEmailStringInput);

export const validateRolesProperty = (fieldName: string, value: string[]): string[] => {
    if (value.length < 1) {
        throw new ErrorFormValidation(fieldName, 'Please assign a user role');
    }
    return value;
};
